import { action, computed, debug, NetbankingAPI, observable } from "aaa";
import { AliasStore } from "components/alias/AliasStore";
import * as _ from "lodash";
import { NetbankingAPIClient } from "network/APIClient";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import profileStore from "stores/ProfileStore";
import { amountToNumber, numberToEuroAmount, ZERO_AMOUNT } from "types/amount";
import { IEbvSecAppActivationSigningResponse, IEbvSecAppPostActivationCodesResponse, ISIdDevice, ISIdStatusResponse } from "types/ebvSecapp";
import { IAuthorizationLimits, IAuthorizationLimitsResponse, IPutAuthorizationLimitsResponse } from "types/settings";
import { ISignInfoResponse } from "types/signing";

export class SIdentitySettingsStore {
    aliasStore: AliasStore;

    @observable status?: ISIdStatusResponse;
    @observable devices?: Array<ISIdDevice> = [];

    @observable limitResponse?: IPutAuthorizationLimitsResponse;
    @observable limits?: IAuthorizationLimitsResponse;
    @observable sidLimit?: IAuthorizationLimits;

    @observable activationCodeResponse?: IEbvSecAppPostActivationCodesResponse;
    @observable addDeviceSigningResponse?: IEbvSecAppActivationSigningResponse;

    @observable deviceToDelete?: ISIdDevice;
    @observable deleteDeviceResponse?: ISignInfoResponse;

    @observable withoutLimit: boolean = true;
    @observable limit: number = 0;
    @observable originalLimit: number = 0;

    @observable isLoadingStatus: boolean = false;
    @observable isLoadingDevices: boolean = false;
    @observable isLoadingLimits: boolean = false;
    @observable isDeletingDevice: boolean = false;

    constructor() {
        this.aliasStore = new AliasStore();
    }

    isLoading(): boolean {
        return this.isLoadingDevices ||
            this.isLoadingStatus ||
            this.isLoadingLimits ||
            this.aliasStore.isLoading ||
            profileStore.isLoading ||
            externalInterfaceStore.isLoadingSettings;
    }

    @action async load() {
        await this.loadSettings();
        await this.loadSIdSettingsStatus();

        if (this.status && this.status.status !== "NOT_ACTIVE") {
            await this.loadSIdSettingsDevices();
            await this.loadLimits();
        }
    }

    @action async loadSettings() {
        if (externalInterfaceStore.settings || externalInterfaceStore.isLoadingSettings) {
            // We've already loaded the settings -> get out
            return;
        }

        await externalInterfaceStore.loadSettings();
    }

    @action async loadLimits() {
        const target = NetbankingAPI.getAuthorizationLimits();
        this.isLoadingLimits = true;

        try {
            this.limits = await NetbankingAPIClient.requestType(target);
            this.sidLimit = _.find(this.limits.limits, limit => limit.authorizationType === "SID");
            if (!this.sidLimit) {
                throw new Error("No SID limit found");
            }

            debug.log("### limits", this.limits, this.sidLimit);

            this.isLoadingLimits = false;
        } catch (error) {
            this.isLoadingLimits = false;
            throw error;
        }
    }

    @action restoreOriginalLimit() {
        this.limit = this.originalLimit;
        this.withoutLimit = this.limit === 0;
    }

    @action async loadSIdSettingsStatus() {
        const target = NetbankingAPI.getSIdSettingsStatus();
        this.isLoadingStatus = true;

        try {
            this.status = await NetbankingAPIClient.requestType(target);
            if (this.status.secAppDailyLimit) {
                this.limit = amountToNumber(this.status.secAppDailyLimit);
                this.originalLimit = this.limit;
                this.withoutLimit = this.limit === 0;
            }
            debug.log("### sIdentitySettingsStatus", this.status);
            this.isLoadingStatus = false;
        } catch (error) {
            this.isLoadingStatus = false;
            throw error;
        }
    }

    @action async loadSIdSettingsDevices() {
        const target = NetbankingAPI.getSIdSettingsDevices();
        this.isLoadingDevices = true;

        try {
            this.devices = await NetbankingAPIClient.requestType(target);
            debug.log("### sIdentityDevices", this.devices);
            this.isLoadingDevices = false;
        } catch (error) {
            this.isLoadingDevices = false;
            throw error;
        }
    }

    @action async deleteSIdSettingsDevice() {
        if (!this.deviceToDelete) {
            return;
        }

        const deviceId = this.deviceToDelete.id.toString();
        const target = NetbankingAPI.deleteSIdSettingsDevice(deviceId);

        try {
            this.isDeletingDevice = true;
            this.deleteDeviceResponse = await NetbankingAPIClient.requestType(target);
            this.isDeletingDevice = false;
        } catch (error) {
            this.isDeletingDevice = false;
            throw error;
        }
    }

    @action async changeLimit() {
        if (!this.sidLimit) {
            return;
        }

        this.sidLimit.dailyLimit = this.withoutLimit === false ? numberToEuroAmount(this.limit) : ZERO_AMOUNT;

        const target = NetbankingAPI.putAuthorizationLimits(this.sidLimit);

        try {
            this.limitResponse = await NetbankingAPIClient.requestType(target);
        } catch (error) {
            throw error;
        }
    }

    @action async addDevice() {
        const target = NetbankingAPI.postActivationCodes();

        try {
            this.activationCodeResponse = await NetbankingAPIClient.requestType(target);
        } catch (error) {
            throw error;
        }
    }

    @computed get activationCode() {
        if (this.addDeviceSigningResponse && this.addDeviceSigningResponse.domainServiceResponse) {
            if (this.addDeviceSigningResponse.domainServiceResponse.activationCodes
                && this.addDeviceSigningResponse.domainServiceResponse.activationCodes.length > 0
            ) {
                return this.addDeviceSigningResponse.domainServiceResponse.activationCodes[0].code;
            } else if (this.addDeviceSigningResponse.domainServiceResponse.activationCode) {
                return this.addDeviceSigningResponse.domainServiceResponse.activationCode.code;
            }
        }

        return "";
    }

    @computed get activationCodeFormatted() {
        const code = this.activationCode;
        // Insert space after every 4th character, then trim trailing space
        return code.replace(/(.{4})/g, "$1 ").trim();
    }

    @computed get canActivateGeorgeId() {
        return !!(
            profileStore.profile?.flags?.includes("georgeIdMigrationRequested") ||
            profileStore.profile?.flags?.includes("georgeIdMigrationMandatory")
        ) && !!profileStore.profile.productPrivileges.includes("george");
    }
}




// WEBPACK FOOTER //
// ./src/components/loginManagement/sIdentitySettings/SIdentitySettingsStore.tsx